import React from 'react';
import { Facebook, Instagram, Linkedin, Link2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { AgentConfig } from '../config/agents';
import { seoConfig } from '../../seo.config';

interface AboutProps {
  agent: AgentConfig;
}

export default function About({ agent }: AboutProps) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO 
        title={seoConfig.about.title}
        description={seoConfig.about.description}
        keywords={seoConfig.about.keywords}
      />
      <Header agent={agent} />
      <main className="flex-grow bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="relative aspect-[4/5] p-2 bg-white border border-gray-300 rounded shadow-sm animate-fade-in">
              <img
                src="/agentphoto.png"
                alt={agent.name}
                className="object-cover w-full h-full grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
            <div className="flex flex-col h-full">
              <div className="mb-4">
                <h1 className="text-4xl font-bold text-navy-900 mb-4 animate-slide-right">About {agent.name}</h1>
                <div className="flex space-x-4 mb-4 animate-slide-right animate-delay-100">
                  {agent.socialLinks.facebook && (
                    <a
                      href={agent.socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:text-navy-900 hover:scale-110 transition-all duration-200"
                      aria-label="Facebook Profile"
                    >
                      <Facebook className="w-6 h-6" />
                    </a>
                  )}
                  {agent.socialLinks.instagram && (
                    <a
                      href={agent.socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:text-navy-900 hover:scale-110 transition-all duration-200"
                      aria-label="Instagram Profile"
                    >
                      <Instagram className="w-6 h-6" />
                    </a>
                  )}
                  {agent.socialLinks.linkedin && (
                    <a
                      href={agent.socialLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:text-navy-900 hover:scale-110 transition-all duration-200"
                      aria-label="LinkedIn Profile"
                    >
                      <Linkedin className="w-6 h-6" />
                    </a>
                  )}
                  {agent.socialLinks.zillow && (
                    <a
                      href={agent.socialLinks.zillow}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:text-navy-900 hover:scale-110 transition-all duration-200"
                      aria-label="Zillow Profile"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 23.283 25.577"
                        className="w-6 h-6"
                        fill="currentColor"
                      >
                        <g fillRule="nonzero">
                          <path d="m15.743 6.897c.117-.026.169.013.24.091.403.448 1.691 2.021 2.041 2.45.065.078.02.163-.032.208-2.6 2.028-5.493 4.901-7.105 6.955-.032.046-.006.046.02.039 2.808-1.209 9.405-3.14 12.376-3.679v-3.763l-11.628-9.198-11.648 9.191v4.114c3.607-2.144 11.953-5.466 15.736-6.408z" />
                          <path d="m6.279 22.705c-.097.052-.176.039-.254-.039l-2.171-2.587c-.058-.072-.065-.111.013-.221 1.678-2.457 5.103-6.286 7.287-7.904.039-.026.026-.059-.02-.039-2.275.741-8.742 3.523-11.134 4.875v8.787h23.277v-8.462c-3.172.539-12.675 3.367-16.998 5.59z" />
                        </g>
                      </svg>
                    </a>
                  )}
                  <a
                    href={agent.socialLinks.retter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-navy-900 hover:scale-110 transition-all duration-200"
                    aria-label="Retter & Company Profile"
                  >
                    <Link2 className="w-6 h-6" />
                  </a>
                </div>
              </div>
              <div className="overflow-y-auto pr-4 space-y-4 text-gray-700 animate-slide-right animate-delay-200 flex-grow" style={{ height: 'calc(100% - 7.5rem)' }}>
                {agent.aboutDescription.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer agent={agent} />
    </div>
  );
}